import { useState } from 'react';

export interface ErrorModalState {
  isOpen: boolean;
  title: string;
  message: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

export const useErrorHandler = () => {
  const [errorModal, setErrorModal] = useState<ErrorModalState>({
    isOpen: false,
    title: '',
    message: ''
  });

  const showError = (
    title: string,
    message: string,
    action?: { label: string; onClick: () => void }
  ) => {
    console.error(`[${title}] ${message}`);
    setErrorModal({
      isOpen: true,
      title,
      message,
      action
    });
  };

  const hideError = () => {
    setErrorModal({
      isOpen: false,
      title: '',
      message: ''
    });
  };

  const handleAsyncError = async (
    asyncFn: () => Promise<any>,
    errorTitle: string,
    fallbackAction?: () => void
  ) => {
    try {
      return await asyncFn();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      showError(errorTitle, errorMessage, fallbackAction ? {
        label: 'Go to Dashboard',
        onClick: () => {
          hideError();
          fallbackAction();
        }
      } : undefined);
      throw error;
    }
  };

  return {
    errorModal,
    showError,
    hideError,
    handleAsyncError
  };
};

export const ErrorModal: React.FC<{
  isOpen: boolean;
  title: string;
  message: string;
  action?: { label: string; onClick: () => void };
  onClose: () => void;
}> = ({ isOpen, title, message, action, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl">
        <div className="flex items-center mb-4">
          <div className="flex-shrink-0">
            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="ml-3 text-lg font-medium text-gray-900">{title}</h3>
        </div>
        
        <div className="mb-6">
          <p className="text-sm text-gray-600">{message}</p>
        </div>
        
        <div className="flex justify-end space-x-3">
          {action && (
            <button
              onClick={action.onClick}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              {action.label}
            </button>
          )}
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}; 